import { Button, Icon, IconName, pxToRem, Text } from '@lbcamden/lib-react';
import { formatDMY, parseDate } from '@lbcamden/shared-utils';
import { DateTime } from 'luxon';
import { FunctionComponent } from 'react';

import { Markdown } from '~/client/components/common/Markdown';
import { FullPcfraPerson, PcfraStatus } from '~/common/types/people';

import { InfoContainer, InfoHeading } from '../ResidentCard.styled';
import {
  ButtonsContainer,
  ContentContainer,
  CommentHeading,
  StatusContainer,
  CommentContainer,
} from './PcfraInfo.styled';
import { PcfraStatusTag } from './PcfraStatusTag';

type Props = {
  person: FullPcfraPerson;
  isPcfraManager?: boolean;
  actionHandlers: PcfraActionHandlers;
};

export type PcfraActionHandlers = {
  onClickRecordAssessment(): void;
  onClickCreateEditPcfra(): void;
  onClickReconfirmSuitability(): void;
  onClickRemoveFromQueue(): void;
  onClickDiscardDraft(): void;
  onClickRevokePcfra(): void;
};

type PcfraAction = {
  icon?: IconName;
  label: string;
  onClick: () => void;
};

const reviewDateText = (pcfraReviewDate: string, isOverdue: boolean) =>
  `Next review: ${formatDMY(pcfraReviewDate)}${isOverdue ? ' (overdue)' : ''}`;

export const PcfraInfo: FunctionComponent<Props> = ({
  person: { pcfraStatus, pcfraStatusComments, pcfraReviewDate },
  isPcfraManager,
  actionHandlers,
}) => {
  const reviewOverdue = DateTime.now() > parseDate(pcfraReviewDate ?? '');

  const getPrimaryAction = (): PcfraAction | undefined => {
    const { onClickRecordAssessment, onClickCreateEditPcfra, onClickReconfirmSuitability } =
      actionHandlers;

    if ([PcfraStatus.None, PcfraStatus.Requested, PcfraStatus.Unnecessary].includes(pcfraStatus)) {
      return { label: 'Record assessment', onClick: onClickRecordAssessment };
    }
    if (pcfraStatus === PcfraStatus.Necessary) {
      return { label: 'Start PCFRA', icon: 'ui/edit-2', onClick: onClickCreateEditPcfra };
    }
    if (pcfraStatus === PcfraStatus.Submitted && isPcfraManager) {
      return { label: 'Review draft', onClick: onClickCreateEditPcfra };
    }
    if ([PcfraStatus.Drafting, PcfraStatus.Submitted].includes(pcfraStatus)) {
      return { label: 'Continue editing', icon: 'ui/edit-2', onClick: onClickCreateEditPcfra };
    }
    if (pcfraStatus === PcfraStatus.Active) {
      return { label: 'Reconfirm suitability', onClick: onClickReconfirmSuitability };
    }
  };

  const getSecondaryAction = (): PcfraAction | undefined => {
    const { onClickRemoveFromQueue, onClickDiscardDraft, onClickRevokePcfra } = actionHandlers;

    if (pcfraStatus === PcfraStatus.Necessary) {
      return { label: 'Remove from queue', onClick: onClickRemoveFromQueue };
    }
    if (pcfraStatus === PcfraStatus.Drafting) {
      return { label: 'Discard draft', icon: 'ui/bin', onClick: onClickDiscardDraft };
    }
    if (pcfraStatus === PcfraStatus.Active) {
      return { label: 'Revoke PCFRA', onClick: onClickRevokePcfra };
    }
  };

  const primaryAction = getPrimaryAction();
  const secondaryAction = getSecondaryAction();

  return (
    <InfoContainer>
      <InfoHeading>PCFRA status</InfoHeading>

      <ContentContainer>
        <StatusContainer>
          <PcfraStatusTag status={pcfraStatus} reviewOverdue={reviewOverdue} />
          {pcfraReviewDate && (
            <Text $color={reviewOverdue ? 'error' : 'text'}>
              {reviewDateText(pcfraReviewDate, reviewOverdue)}
            </Text>
          )}
        </StatusContainer>

        <ButtonsContainer>
          {primaryAction && (
            <Button onClick={primaryAction.onClick}>
              {primaryAction.icon && <Icon icon={primaryAction.icon} width={pxToRem(18)} />}
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button $variant="secondary" onClick={secondaryAction.onClick}>
              {secondaryAction.icon && <Icon icon={secondaryAction.icon} width={pxToRem(18)} />}
              {secondaryAction.label}
            </Button>
          )}
        </ButtonsContainer>
      </ContentContainer>

      {pcfraStatusComments && (
        <>
          <CommentHeading>Comment</CommentHeading>

          <CommentContainer>
            <Markdown>{pcfraStatusComments}</Markdown>
          </CommentContainer>
        </>
      )}
    </InfoContainer>
  );
};
